import { ITransport } from '../../domain/interfaces/ITransport';
import { LogEntry } from '../../domain/types';

export interface HttpTransportConfig {
  endpoint: string;
  batchInterval?: number;
  maxBatchSize?: number;
  maxRetries?: number;
  baseDelay?: number;
}

export class HttpTransport implements ITransport {
  private endpoint: string;
  private apiKey?: string;
  private queue: LogEntry[] = [];
  private batchTimer: number | null = null;
  private batchInterval: number;
  private maxBatchSize: number;
  private retryCount = 0;
  private maxRetries: number;
  private baseDelay: number;

  constructor(config: HttpTransportConfig) {
    this.endpoint = config.endpoint;
    this.batchInterval = config.batchInterval ?? 2000;
    this.maxBatchSize = config.maxBatchSize ?? 50;
    this.maxRetries = config.maxRetries ?? 3;
    this.baseDelay = config.baseDelay ?? 1000;
  }

  setApiKey(apiKey: string): void {
    this.apiKey = apiKey;
  }

  async send(entries: LogEntry[]): Promise<void> {
    this.queue.push(...entries);
    
    if (this.queue.length >= this.maxBatchSize) {
      await this.flush();
    } else {
      this.scheduleBatch();
    }
  }

  async flush(): Promise<void> {
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }

    if (this.queue.length === 0) return;

    const batch = this.queue.splice(0, this.maxBatchSize);
    
    try {
      await this.sendBatch(batch);
      this.retryCount = 0;
    } catch (error) {
      // Put failed items back in queue for retry
      this.queue.unshift(...batch);
      await this.handleError(error);
    }
  }

  private scheduleBatch(): void {
    if (this.batchTimer) return;
    
    this.batchTimer = window.setTimeout(() => {
      this.batchTimer = null;
      this.flush();
    }, this.batchInterval);
  }

  private async sendBatch(batch: LogEntry[]): Promise<void> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    };

    if (this.apiKey) {
      headers['X-API-Key'] = this.apiKey;
    }

    const response = await fetch(this.endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify(batch)
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
  }

  private async handleError(error: any): Promise<void> {
    console.error('[StackScope] Failed to send logs:', error);
    
    if (this.retryCount < this.maxRetries) {
      const delay = this.baseDelay * Math.pow(2, this.retryCount);
      this.retryCount++;
      
      console.log(`[StackScope] Retrying in ${delay}ms (attempt ${this.retryCount}/${this.maxRetries})`);
      
      setTimeout(() => {
        this.flush();
      }, delay);
    } else {
      console.error('[StackScope] Max retries exceeded, dropping batch');
      this.queue = []; // Clear queue to prevent memory leak
      this.retryCount = 0;
    }
  }
}